"use client";

import { useState, useEffect, FormEvent, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { Eye, EyeOff } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { track } from "../../lib/analytics";
import { GoogleButton } from "../components/GoogleButton";

function passwordStrength(pw: string): { label: string; score: 0 | 1 | 2 | 3 | 4; color: string } {
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw) && /[^\w]/.test(pw)) score++;
  const scores: Array<{ label: string; color: string }> = [
    { label: "too short", color: "bg-red-400" },
    { label: "weak", color: "bg-amber-400" },
    { label: "okay", color: "bg-amber-500" },
    { label: "good", color: "bg-emerald-500" },
    { label: "strong", color: "bg-emerald-600" },
  ];
  const s = scores[score];
  return { label: s.label, score: score as 0 | 1 | 2 | 3 | 4, color: s.color };
}

export default function SignupPage() {
  return (
    <Suspense fallback={<main className="min-h-[60vh] flex items-center justify-center"><p className="text-sm text-maren-charcoal/50 dark:text-maren-stone/55">Loading…</p></main>}>
      <SignupInner />
    </Suspense>
  );
}

// Supabase returns server-side validation errors with messages like
// "Email address 'x@example.com' is invalid" — hard for a first-time
// visitor to action. Map the common ones to something they can actually
// do something about before showing the raw message.
function humanizeAuthError(raw: string): string {
  const lower = raw.toLowerCase();
  if (lower.includes("invalid") && lower.includes("email")) {
    return "That email was rejected by our provider — some test/disposable domains (like example.com, mailinator) are blocked. Please use the address you'll actually study from.";
  }
  if (lower.includes("already registered") || lower.includes("already been registered") || lower.includes("user already")) {
    return "An account with that email already exists. Try signing in instead.";
  }
  if (lower.includes("password") && lower.includes("short")) {
    return "Password needs to be at least 8 characters.";
  }
  return raw;
}

// Per Hermes audit fix I2: segmented CTAs from the homepage should
// carry through to onboarding copy. Each value here is locked to a
// segment key the homepage links to via /signup?segment=<key>.
const SEGMENT_COPY: Record<
  string,
  { heading: string; subhead: string }
> = {
  retaker: {
    heading: "Retake Mode starts here.",
    subhead:
      "Create your account, then import your previous attempt and rebuild your plan around your weakest sections.",
  },
  first_time: {
    heading: "Build your week-one plan.",
    subhead:
      "After signup, Maren walks you through your test date, your resources, and your first missed question.",
  },
  non_trad: {
    heading: "Build around your schedule.",
    subhead:
      "After signup, Maren plans against the hours you actually have. Ninety minutes a night counts.",
  },
  plateau: {
    heading: "Break the 510 plateau.",
    subhead:
      "After signup, Maren reads your full-length history and names the three to five patterns costing you points.",
  },
};

function SignupInner() {
  const params = useSearchParams();
  const preselectedPlan = params.get("plan");
  // Purchasable checkout targets now include the one-time study passes,
  // not just the monthly tiers. Drive the redirect + copy off one predicate
  // (and one label) so every branch stays in sync.
  // "pro" is retired and deliberately absent: an old ?plan=pro link signs
  // up normally and lands on the dashboard, never a retired checkout.
  const isPaidTarget =
    preselectedPlan === "max" ||
    preselectedPlan === "pass_3mo" ||
    preselectedPlan === "pass_6mo";
  const planLabel =
    preselectedPlan === "max"
      ? "monthly access"
      : preselectedPlan === "pass_3mo"
        ? "the 3-Month Study Pass"
        : preselectedPlan === "pass_6mo"
          ? "the 6-Month Study Pass"
          : "";
  const segment = params.get("segment");
  const segmentCopy = segment ? SEGMENT_COPY[segment] : undefined;
  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [status, setStatus] = useState<"idle" | "loading" | "error" | "success">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [fieldError, setFieldError] = useState<"email" | "password" | "terms" | null>(null);
  const { session, loading } = useAuth();
  const router = useRouter();

  // Inline email validity for H6. We only show the error after first
  // blur so the form doesn't yell at the user mid-type.
  const emailLooksValid = /^\S+@\S+\.\S+$/.test(email.trim());
  const showInlineEmailError = emailTouched && email.length > 0 && !emailLooksValid;
  const canSubmit = emailLooksValid && password.length > 0 && acceptedTerms;

  // Once a session exists -- whether the visitor arrived already logged
  // in, or just signed up with email confirmation disabled -- send them
  // onward. A preselected paid plan goes straight to checkout; everyone
  // else to the dashboard (which bounces new users to /welcome).
  useEffect(() => {
    if (loading || !session) return;
    if (isPaidTarget) {
      router.replace(`/billing/start?plan=${preselectedPlan}`);
    } else {
      router.replace("/dashboard");
    }
  }, [session, loading, preselectedPlan, isPaidTarget, router]);

  // Top of the signup funnel: one event per page visit, only for
  // visitors who aren't already signed in (signup_complete is the
  // bottom of the same funnel).
  useEffect(() => {
    if (loading || session) return;
    track({ name: "signup_started", props: { plan: preselectedPlan } });
    // Fire once per visit: preselectedPlan is fixed for the page's life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFieldError(null);
    setTriedSubmit(true);
    if (!acceptedTerms) {
      setStatus("error");
      setFieldError("terms");
      setErrorMsg("Please accept the Terms, Privacy Policy, and Disclaimer to continue.");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) {
      setStatus("error");
      const msg = humanizeAuthError(error.message);
      setErrorMsg(msg);
      setFieldError(msg.includes("email") || msg.includes("address") ? "email" : null);
    } else {
      // Top of the funnel. Count the attempt regardless of whether email
      // confirmation (if on) completes.
      track({ name: "signup_complete", props: { method: "email" } });
      // Carry the homepage segment through onboarding. The welcome step
      // reads + clears this on first mount so the user doesn't re-pick
      // first-time vs retaker. Restricted to the four known segment keys.
      if (segment && segment in SEGMENT_COPY) {
        try {
          localStorage.setItem("maren_signup_segment", segment);
        } catch {
          // localStorage can throw in privacy modes; harmless to skip.
        }
      }
      // When email confirmation is OFF, signUp returns a live session and
      // the user is signed in immediately -> skip the "check your email"
      // screen and continue (to checkout for a paid plan). When it's ON,
      // there's no session yet; fall through to the confirm-email screen.
      if (data.session) {
        const paid = isPaidTarget;
        router.replace(paid ? `/billing/start?plan=${preselectedPlan}` : "/dashboard");
        return;
      }
      setStatus("success");
    }
  }

  const input =
    "w-full rounded-md border border-maren-sage/50 dark:border-maren-sage/30 bg-white dark:bg-maren-charcoal px-3 py-2 text-sm text-maren-charcoal dark:text-maren-stone placeholder-maren-charcoal/40 dark:placeholder-maren-stone/40 dark:placeholder-maren-stone/40 outline-none focus:border-maren-green focus:ring-2 focus:ring-maren-gold/30 transition";
  const label = "block text-xs font-semibold uppercase tracking-wide text-maren-green dark:text-maren-gold mb-1";

  const strength = password ? passwordStrength(password) : null;

  if (status === "success") {
    const paid = isPaidTarget;
    return (
      <main className="flex items-center justify-center min-h-[calc(100vh-56px)] px-4">
        <div className="w-full max-w-sm text-center">
          <div className="bg-white dark:bg-maren-charcoal rounded-xl shadow-sm border border-maren-sage/40 dark:border-maren-sage/20 p-8">
            <p className="text-2xl mb-3">✓</p>
            <h2 className="text-base font-bold text-maren-charcoal dark:text-maren-stone mb-1">Check your email</h2>
            <p className="text-sm text-maren-charcoal/60 dark:text-maren-stone/60">
              We sent a confirmation link to <span className="font-medium text-maren-charcoal dark:text-maren-stone">{email}</span>.
              Click it to activate your account, then sign in.
            </p>
            {paid && (
              <p className="mt-3 text-xs text-maren-charcoal/60 dark:text-maren-stone/60">
                After you confirm and sign in, we&apos;ll take you straight to checkout for {planLabel}.
              </p>
            )}
            <Link
              href={paid ? `/login?next=${encodeURIComponent(`/billing/start?plan=${preselectedPlan}`)}` : "/login"}
              className="mt-5 inline-block text-sm font-semibold text-maren-green dark:text-maren-gold hover:text-maren-green dark:text-maren-gold"
            >
              Go to sign in →
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex items-center justify-center min-h-[calc(100vh-56px)] px-4">
      <div className="w-full max-w-sm">
        <div className="mb-7 text-center">
          <h1 className="text-xl font-display font-medium text-maren-charcoal dark:text-maren-stone">
            {segmentCopy?.heading ?? "Create your account"}
          </h1>
          <p className="text-sm text-maren-charcoal/60 dark:text-maren-stone/60 mt-1">
            {isPaidTarget
              ? `You picked ${planLabel}. Create your account, then you will go to checkout.`
              : segmentCopy?.subhead ?? "Free to get started, no card required."}
          </p>
        </div>

        <div className="bg-white dark:bg-maren-charcoal rounded-xl shadow-sm border border-maren-sage/40 dark:border-maren-sage/20 p-8">
          {/* Google is the promoted path: it skips the email-confirmation
              step entirely (Google addresses are pre-verified), so it's the
              fastest way into the free tracker. The email form stays below
              for anyone who prefers it. Segment + preselected plan carry
              through the OAuth redirect. */}
          <GoogleButton segment={segment} plan={preselectedPlan} />
          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-maren-sage/40 dark:bg-maren-sage/20" />
            <span className="text-xs text-maren-charcoal/50 dark:text-maren-stone/50">or</span>
            <div className="h-px flex-1 bg-maren-sage/40 dark:bg-maren-sage/20" />
          </div>

          {status === "error" && fieldError === null && (
            <div className="rounded-md bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800 px-4 py-3 text-sm text-red-800 dark:text-red-300 mb-5">
              {errorMsg}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <div>
              <label className={label} htmlFor="signup-email">Email</label>
              <input
                id="signup-email"
                required
                type="email"
                autoComplete="email"
                placeholder="you@gmail.com"
                // Cursor lands in email on page open so the visitor can
                // start typing immediately without a click. Reduces
                // first-action latency on the highest-leverage form in
                // the funnel.
                autoFocus
                aria-invalid={fieldError === "email" || showInlineEmailError}
                aria-describedby={
                  fieldError === "email"
                    ? "signup-email-err"
                    : showInlineEmailError
                    ? "signup-email-inline-err"
                    : undefined
                }
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (fieldError === "email") setFieldError(null);
                }}
                onBlur={() => setEmailTouched(true)}
                className={`${input} ${(fieldError === "email" || showInlineEmailError) ? "!border-red-400 focus:!border-red-500 focus:!ring-red-500/20" : ""}`}
              />
              {fieldError === "email" && (
                <p id="signup-email-err" className="mt-1 text-xs text-red-600 dark:text-red-400">
                  {errorMsg}
                </p>
              )}
              {!fieldError && showInlineEmailError && (
                <p id="signup-email-inline-err" className="mt-1 text-xs text-red-600 dark:text-red-400" aria-live="polite">
                  Enter a valid email address.
                </p>
              )}
            </div>
            <div>
              <label className={label} htmlFor="signup-password">Password</label>
              <div className="relative">
                <input
                  id="signup-password"
                  required
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={input + " pr-10"}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-maren-charcoal/50 dark:text-maren-stone/55 hover:text-maren-charcoal dark:hover:text-maren-stone dark:hover:text-slate-200 p-1"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {strength && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1 rounded-full bg-maren-sage/30 dark:bg-maren-sage/15 overflow-hidden">
                    <div
                      className={`h-full transition-all ${strength.color}`}
                      style={{ width: `${(strength.score / 4) * 100}%` }}
                    />
                  </div>
                  <span className="text-[11px] text-maren-charcoal/60 dark:text-maren-stone/60 w-14 text-right">
                    {strength.label}
                  </span>
                </div>
              )}
            </div>
            {/* Simplified terms checkbox. Standalone input + label-for
                pairing — no wrapping div onClick, no stopPropagation
                gymnastics. The browser handles label-input toggling
                natively for clicks anywhere on the label text, EXCEPT on
                the nested <Link> elements which navigate instead. That's
                exactly the behavior we want. The previous wrapping-div
                fix was overcomplicated and the founder reported it still
                left the React state out of sync in some clicks. */}
            <div className="flex items-start gap-2">
              <input
                id="signup-terms"
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => {
                  setAcceptedTerms(e.target.checked);
                  // Observability: surfaces in DevTools that the change
                  // event DID fire. If you ever see this missing on a
                  // visible toggle, the issue is upstream (HMR cache,
                  // browser quirk).
                  if (typeof window !== "undefined") {
                    console.debug("signup_terms_changed", e.target.checked);
                  }
                }}
                className="mt-0.5 h-4 w-4 rounded border-maren-sage/50 dark:border-maren-sage/30 text-maren-green dark:text-maren-gold focus:ring-maren-gold/40 cursor-pointer shrink-0"
              />
              <label
                htmlFor="signup-terms"
                className="text-xs text-maren-charcoal/65 dark:text-maren-stone/65 leading-relaxed cursor-pointer select-none"
              >
                I agree to the{" "}
                <Link href="/terms" target="_blank" className="text-maren-green dark:text-maren-gold hover:underline">
                  Terms of Service
                </Link>
                ,{" "}
                <Link href="/privacy" target="_blank" className="text-maren-green dark:text-maren-gold hover:underline">
                  Privacy Policy
                </Link>
                , and{" "}
                <Link href="/disclaimer" target="_blank" className="text-maren-green dark:text-maren-gold hover:underline">
                  Disclaimer
                </Link>
                . I understand Maren is a study tool, not a guarantee of exam
                performance, and AI-generated content may contain errors.
                {triedSubmit && !acceptedTerms && (
                  <span
                    className="ml-1 inline-block rounded-sm bg-red-100 dark:bg-red-900/30 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-700 dark:text-red-300 align-middle"
                    aria-live="polite"
                  >
                    Required
                  </span>
                )}
              </label>
            </div>
            {(() => {
              const disabled =
                status === "loading" ||
                password.length < 8 ||
                !acceptedTerms ||
                !emailLooksValid;
              const hint =
                !emailLooksValid && email.length > 0
                  ? "Enter a valid email address."
                  : password.length > 0 && password.length < 8
                  ? "Password needs at least 8 characters."
                  : !acceptedTerms
                  ? "You need to check the box above to continue."
                  : null;
              return (
                <div>
                  <button
                    type="submit"
                    disabled={disabled}
                    className="w-full rounded-md bg-maren-green px-4 py-3 text-sm font-bold text-maren-stone hover:bg-maren-green/90 disabled:opacity-50 transition"
                  >
                    {status === "loading" ? "Creating account…" : "Create Account"}
                  </button>
                  {disabled && hint && status !== "loading" && (
                    <p className="mt-2 text-xs text-maren-charcoal/60 dark:text-maren-stone/60 text-center">{hint}</p>
                  )}
                </div>
              );
            })()}
          </form>

          {/* Email confirmation is OFF in Supabase. Every one of the 94
              accounts created in the last 60 days was confirmed instantly
              (max lag 0 seconds), so no email is sent and nobody has to
              leave the page. The previous line here promised "a
              confirmation email to verify your address before signing in",
              inventing a delay at the exact moment someone is reaching for
              their card. The post-submit screen still handles the
              confirmation flow correctly if that setting is ever switched
              back on; this pre-submit line just no longer lies about it. */}
          <p className="mt-3 text-center text-xs text-maren-charcoal/55 dark:text-maren-stone/55">
            {isPaidTarget
              ? "Takes a few seconds. You go straight to checkout next."
              : "You will be signed in right away. No confirmation email to wait for."}
          </p>

          <p className="text-center text-sm text-maren-charcoal/60 dark:text-maren-stone/60 mt-5">
            Already have an account?{" "}
            {/* Carries the chosen plan through the round trip. Now that
                /billing/start sends unauthenticated buyers here rather than
                to login, this is the return path for existing customers --
                a bare /login would drop the plan and land them on the
                dashboard with their purchase intent gone. */}
            <Link
              href={
                isPaidTarget
                  ? `/login?next=${encodeURIComponent(`/billing/start?plan=${preselectedPlan}`)}`
                  : "/login"
              }
              className="text-maren-green dark:text-maren-gold font-semibold hover:text-maren-green dark:text-maren-gold"
            >
              Sign in
            </Link>
          </p>
        </div>

        {/* Trust + AI accuracy reassurance per Hermes audit fix I8.
            Sits adjacent to the signup CTA, not buried in legal. */}
        <p className="mt-5 text-center text-[11px] text-maren-charcoal/55 dark:text-maren-stone/55 leading-relaxed">
          Your study data is private. We do not sell it. AI outputs may contain errors;
          verify against official materials.
        </p>
      </div>
    </main>
  );
}
